import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, MapPin, Store, ShoppingBag, Headphones } from "lucide-react";
import { SHOWROOM, SHOWROOM_GROUPS } from "../data/showroom";
import { SITE } from "../data/site";
import { Reveal } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";
import { PrimaryCTA } from "../components/CTA";
import { FloatingIcons } from "../components/FloatingIcons";
import { DeviceDrift } from "../components/DeviceDrift";
import { TechRail } from "../components/TechRail";
import { SolarSystem } from "../components/SolarSystem";
import { Tilt3D } from "../components/Motion";
import { TechVideo } from "../components/TechVideo";
import { PhraseFloat } from "../components/Motion";
import { GlowFrame } from "../components/GlowFrame";
import { LiveIcon } from "../components/LiveIcon";
import { useLang } from "../i18n";

export default function Showroom() {
  const { t } = useLang();
  const [group, setGroup] = useState("all");
  const items = SHOWROOM.filter((i) => group === "all" || i.group === group);

  return (
    <>
      <section data-testid="showroom-hero" className="grain relative overflow-hidden pt-28 sm:pt-36 lg:pt-44 pb-8 sm:pb-12">
        <div className="pointer-events-none absolute -top-24 -right-32 h-[26rem] w-[26rem] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <FloatingIcons
          items={[
            { Icon: ShoppingBag, x: "88%", y: "24%", dur: 10, opacity: "text-magenta/20" },
            { Icon: Headphones, x: "7%", y: "60%", dur: 12, delay: 0.6, opacity: "text-eblue/20" },
          ]}
        />
        <DeviceDrift device="watch-ultra" className="right-[4%] top-6" imgClassName="h-[90px] sm:h-[140px] lg:h-[220px]" dur={10} opacity="opacity-50 lg:opacity-90" />
        <DeviceDrift device="watch-galaxy" className="left-[26%] top-[3%] xl:left-[30%] xl:top-[4%]" imgClassName="h-[56px] lg:h-[120px]" dur={12} delay={0.5} opacity="opacity-40 lg:opacity-60" />
        <DeviceDrift device="moon" className="left-[6%] bottom-[2%]" imgClassName="h-[46px] lg:h-[90px]" dur={18} opacity="opacity-50 lg:opacity-75" />
        <PhraseFloat text={t("showroom.groups.protection")} tone="volt" className="left-[20%] bottom-[10%] xl:left-[26%] xl:bottom-[14%]" dur={10} />
        <PhraseFloat text={t("showroom.groups.power")} tone="cyan" className="right-[24%] top-[46%] xl:right-[30%] xl:top-[40%]" dur={12} delay={0.9} />
        <DeviceDrift device="gaming" className="right-[14%] top-40 xl:right-[16%] xl:top-44" imgClassName="h-[60px] lg:h-[130px]" dur={12} delay={0.7} opacity="opacity-35 lg:opacity-50" />
        <DeviceDrift device="keyboard" className="right-[2%] top-[16rem] xl:top-[19rem]" imgClassName="h-[44px] lg:h-[90px]" dur={14} delay={1.2} opacity="opacity-30 lg:opacity-40" />
        <TechRail
          testid="showroom-tech-rail"
          className="absolute left-1 top-[55%] h-[30vh] w-36 -translate-y-1/2 opacity-70 xl:left-5 xl:h-[44vh] xl:w-72 xl:opacity-100"
          items={[
            { label: t("showroom.items.audio.title"), img: "/devices/airpods.png" },
            { label: t("showroom.items.smartwatches.title"), img: "/devices/watch-ultra.png" },
            { label: t("showroom.items.speakers.title"), img: "/devices/speaker.png" },
            { label: t("showroom.items.keyboards.title"), img: "/devices/keyboard-rgb.png" },
            { label: t("showroom.items.phones.title"), img: "/phones/iphone.png" },
            { label: t("showroom.items.audio.title"), img: "/devices/gaming.png" },
            { label: t("showroom.items.smartwatches.title"), img: "/devices/watch-sport.png" },
            { label: t("showroom.items.phones.title"), img: "/phones/pixel.png" },
          ]}
          speed={30}
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <SectionHead
            chapter={t("showroom.chapter")}
            eyebrow={t("showroom.eyebrow")}
            title={<>{t("showroom.titleA")} <span className="italic text-accent-bright">{t("showroom.titleEm")}</span> {t("showroom.titleB")}</>}
            lede={t("showroom.lede")}
          />
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Showroom categories">
              {SHOWROOM_GROUPS.map((g) => (
                <button
                  key={g}
                  type="button"
                  role="tab"
                  aria-selected={group === g}
                  data-testid={`showroom-filter-${g}`}
                  onClick={() => setGroup(g)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    group === g
                      ? "border-ink bg-ink text-paper"
                      : "border-ink/15 bg-white/5 text-ink/70 hover:border-ink/40 hover:text-ink"
                  }`}
                >
                  {t(`showroom.groups.${g}`)}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* scroll-through solar system of the shelf */}
      <section className="relative overflow-hidden border-b border-sand/60" aria-label={t("showroom.title")}>
        <SolarSystem
          testid="showroom-solar-system"
          center={{ src: "/planets/earth.png", ring: true, glow: "rgba(77,164,255,0.6)" }}
          bodies={[
            { src: "/devices/airpods.png", label: t("showroom.groups.audio"), ring: 0, phase: 20, dur: 26, size: 64, glow: "rgba(244,244,239,0.4)" },
            { src: "/devices/watch-ultra.png", label: t("showroom.groups.wearables"), ring: 0, phase: 140, dur: 26, size: 66, glow: "rgba(233,180,76,0.45)" },
            { src: "/devices/headphones.png", ring: 0, phase: 260, dur: 26, size: 58, glow: "rgba(124,131,219,0.4)", decorative: true },
            { src: "/devices/speaker.png", label: t("showroom.groups.audio"), ring: 1, phase: 90, dur: 38, size: 88, glow: "rgba(61,111,224,0.5)" },
            { src: "/devices/gaming.png", label: t("showroom.groups.studio"), ring: 1, phase: 270, dur: 38, size: 80, glow: "rgba(124,131,219,0.45)" },
            { src: "/devices/watch-galaxy.png", label: t("showroom.groups.wearables"), ring: 1, phase: 180, dur: 38, size: 62, glow: "rgba(61,111,224,0.45)" },
            { src: "/devices/keyboard-rgb.png", label: t("showroom.groups.studio"), ring: 2, phase: 150, dur: 52, size: 96, glow: "rgba(61,111,224,0.5)" },
            { src: "/devices/laptop.png", ring: 2, phase: 330, dur: 52, size: 100, glow: "rgba(127,224,195,0.45)" },
            { src: "/devices/watch-sport.png", ring: 2, phase: 40, dur: 52, size: 56, glow: "rgba(79,179,191,0.4)", decorative: true },
            { src: "/planets/earth.png", ring: 2, phase: 240, dur: 52, size: 46, glow: "rgba(77,164,255,0.55)", decorative: true },
          ]}
        />
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 sm:pb-32 lg:px-16">
        <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {items.map((item) => {
              const text = t(`showroom.items.${item.id}`);
              return (
                <motion.article
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`showroom-item-${item.id}`}
                  className="group"
                >
                  <Tilt3D>
                  <GlowFrame tone={item.id === "keyboards" ? "cyan" : item.fit === "contain" ? "volt" : "clay"} className="[transform-style:preserve-3d]">
                    <div className="relative overflow-hidden rounded-[18px] bg-cream" style={item.fit === "contain" ? { background: "radial-gradient(circle at 50% 60%, rgba(139,92,255,0.25), rgba(11,17,27,1) 70%)" } : undefined}>
                      {item.id === "keyboards" && <div className="rgb-live" aria-hidden="true" />}
                      <img
                        src={item.image}
                        alt={item.alt}
                        loading="lazy"
                        className={`relative aspect-[4/3] w-full transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07] ${
                          item.fit === "contain" ? "object-contain p-6" : "object-cover"
                        }`}
                      />
                      <span className="absolute start-4 top-4 rounded-full bg-espresso/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-soft backdrop-blur">
                        {t(`showroom.groups.${item.group}`)}
                      </span>
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    </div>
                  </GlowFrame>
                  </Tilt3D>
                  <div className="px-1 pt-5">
                    <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight transition-colors group-hover:text-accent-bright">
                      {text.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{text.blurb}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
                      {t("showroom.availability")}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-14">
          <TechVideo src="/videos/bubbles.mp4" testid="showroom-video-bubbles" className="aspect-[21/9] w-full" />
        </Reveal>

        <Reveal className="mt-20">
          <div
            data-testid="showroom-visit-strip"
            className="glass-liquid grain relative overflow-hidden rounded-[24px] px-6 py-12 sm:px-12 sm:py-16"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent/25 blur-3xl" aria-hidden="true" />
            <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="eyebrow !text-gold-soft flex items-center gap-2">
                  <Store className="h-4 w-4" /> {t("showroom.stripEyebrow")}
                  <LiveIcon variant="spin" color="#C99B3F" className="!h-7 !w-7 !rounded-lg" />
                </p>
                <h2 className="mt-4 max-w-xl font-display text-3xl sm:text-4xl font-medium tracking-tight">
                  {t("showroom.stripTitle")}
                </h2>
                <p className="mt-3 flex items-center gap-2 text-ink/65">
                  <MapPin className="h-4 w-4 text-accent-soft" />
                  {SITE.address} · {SITE.locationCue}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <PrimaryCTA to="/visit" testid="showroom-visit-cta" dark>{t("showroom.stripVisit")}</PrimaryCTA>
                <Link
                  to="/quote"
                  data-testid="showroom-quote-link"
                  className="group inline-flex items-center gap-2 rounded-full border border-ink/30 px-7 py-3.5 text-sm sm:text-base font-semibold transition-colors hover:bg-ink/10"
                >
                  {t("showroom.stripQuote")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
